<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { ChevronDown } from 'lucide-vue-next';
import { normalizePath } from '@/nav/menuModel';
import { resolveMenuIcon } from '@/nav/lucideNavIcons';
import type { NormalPageContentSidebarItem } from '@/nav/normalPageSidebar';

const props = withDefaults(
  defineProps<{
    title?: string;
    items: NormalPageContentSidebarItem[];
  }>(),
  { title: 'Navigation' }
);

const route = useRoute();
const isOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const currentPath = computed(() => normalizePath(route.path));

function isActive(url: string): boolean {
  return currentPath.value === normalizePath(url);
}

const activeItem = computed(() => {
  const found = props.items.find((item) => isActive(item.url));
  return found ?? props.items[0];
});

const activeIcon = computed(() => resolveMenuIcon(activeItem.value?.icon ?? undefined));

function onDocMouseDown(e: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    isOpen.value = false;
  }
}

onMounted(() => document.addEventListener('mousedown', onDocMouseDown));
onUnmounted(() => document.removeEventListener('mousedown', onDocMouseDown));
</script>

<template>
  <div class="sidebar-nav">
    <div class="sidebar-nav__mobile" ref="dropdownRef">
      <button
        type="button"
        class="sidebar-nav__mobile-trigger"
        :aria-expanded="isOpen"
        aria-controls="sidebar-nav-dropdown"
        @click="isOpen = !isOpen"
      >
        <span class="sidebar-nav__mobile-trigger-inner">
          <component :is="activeIcon" class="sidebar-nav__icon" aria-hidden="true" />
          {{ activeItem?.title }}
        </span>
        <ChevronDown class="sidebar-nav__chevron" :class="{ 'sidebar-nav__chevron--open': isOpen }" />
      </button>
      <div
        v-show="isOpen"
        id="sidebar-nav-dropdown"
        class="sidebar-nav__dropdown"
        role="navigation"
        :aria-label="title"
      >
        <RouterLink
          v-for="(item, index) in items"
          :key="item.id"
          :to="item.url"
          class="sidebar-nav__dropdown-link"
          :class="{
            'sidebar-nav__link--active': isActive(item.url),
            'sidebar-nav__dropdown-link--border': index !== items.length - 1,
          }"
          @click="isOpen = false"
        >
          <component
            :is="resolveMenuIcon(item.icon ?? undefined)"
            class="sidebar-nav__icon sidebar-nav__icon--sm"
            aria-hidden="true"
          />
          <span>{{ item.title }}</span>
        </RouterLink>
      </div>
    </div>

    <nav class="sidebar-nav__desktop" :aria-label="title">
      <RouterLink
        v-for="item in items"
        :key="item.id"
        :to="item.url"
        class="sidebar-nav__desktop-link"
        :class="{ 'sidebar-nav__link--active': isActive(item.url) }"
      >
        <component
          :is="resolveMenuIcon(item.icon ?? undefined)"
          class="sidebar-nav__icon sidebar-nav__icon--md"
          aria-hidden="true"
        />
        <span>{{ item.title }}</span>
      </RouterLink>
    </nav>
  </div>
</template>

<style scoped>
.sidebar-nav {
  box-sizing: border-box;
  position: relative;
  z-index: 20;
  height: fit-content;
  padding: 1rem 1.5rem;
  border-radius: 6px;
  border: 1px solid var(--side-nav-border, #e5e7eb);
  background: var(--color-surface, #fff);
}

@media (min-width: 1024px) {
  /*
   * Sticky vs document scroll (App.vue keeps main overflow: visible so the window scrolls,
   * like the prototype). Pins the card below the fixed site header (see --site-header-height).
   */
  .sidebar-nav {
    position: sticky;
    /* ~prototype 7.5rem band: header + small gap below fixed nav */
    top: calc(var(--site-header-height, 100px) + 1.25rem);
    margin-top: -62px;
    margin-bottom: 0;
    padding: 1rem 1.5rem;
  }
}

@media (max-width: 1023px) {
  /*
   * Prototype uses -mt-[130px] only when the sidebar row is the first block directly under
   * the hero (py-0 wrapper). Here the hero is followed by Quick Links + header margin, so a
   * large negative margin pulls the card up into the hero image. Keep normal flow so the
   * control sits below the hero band (like the prototype card under the image area).
   */
  .sidebar-nav {
    margin-top: 0;
    margin-bottom: 2rem;
  }
}

.sidebar-nav__mobile {
  display: block;
}

@media (min-width: 1024px) {
  .sidebar-nav__mobile {
    display: none;
  }
}

.sidebar-nav__desktop {
  display: none;
}

@media (min-width: 1024px) {
  .sidebar-nav__desktop {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
}

.sidebar-nav__mobile-trigger {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 1rem;
  border: 1px solid var(--side-nav-border, #e5e7eb);
  border-radius: 6px;
  background: var(--color-surface, #fff);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text, #111);
  cursor: pointer;
  transition: background 0.2s ease;
}

.sidebar-nav__mobile-trigger:hover {
  background: color-mix(in srgb, var(--color-primary, #047857) 5%, transparent);
}

.sidebar-nav__mobile-trigger-inner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sidebar-nav__chevron {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.sidebar-nav__chevron--open {
  transform: rotate(180deg);
}

.sidebar-nav__dropdown {
  position: absolute;
  left: 1rem;
  right: 1rem;
  margin-top: 0.5rem;
  border: 1px solid var(--side-nav-border, #e5e7eb);
  border-radius: 6px;
  background: var(--color-surface, #fff);
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.08);
  overflow: hidden;
  z-index: 50;
}

.sidebar-nav__dropdown-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  color: var(--color-text-muted, #6b7280);
  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.sidebar-nav__dropdown-link--border {
  border-bottom: 1px solid var(--side-nav-border, #e5e7eb);
}

.sidebar-nav__dropdown-link:hover {
  color: var(--color-primary, #047857);
  background: color-mix(in srgb, var(--color-primary, #047857) 5%, transparent);
}

.sidebar-nav__desktop-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-radius: 3px;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  color: var(--color-text-muted, #6b7280);
  transition:
    color 0.2s ease,
    background 0.2s ease;
}

@media (min-width: 640px) {
  .sidebar-nav__desktop-link {
    padding: 0.75rem 1rem;
    font-size: 1rem;
  }
}

.sidebar-nav__desktop-link:hover {
  color: var(--color-primary, #047857);
  background: color-mix(in srgb, var(--color-primary, #047857) 5%, transparent);
}

.sidebar-nav__link--active {
  background: var(--color-primary, #047857);
  color: var(--color-button-foreground, #fff);
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.06);
}

.sidebar-nav__link--active:hover {
  background: var(--color-primary, #047857);
  color: var(--color-button-foreground, #fff);
}

.sidebar-nav__icon {
  flex-shrink: 0;
}

.sidebar-nav__icon--sm {
  width: 1rem;
  height: 1rem;
}

.sidebar-nav__icon--md {
  width: 1rem;
  height: 1rem;
}

@media (min-width: 640px) {
  .sidebar-nav__icon--md {
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
